import React from 'react';
import { Box, Button, Chip, MenuItem, TextField, Typography } from '@mui/material';
import { AWS_REGIONS } from '../../../shared/constants/aws';
import { TOUCH_TARGET_SX } from '../../../shared/constants/layout';
import { useViewport } from '../../../shared/hooks/useViewport';
import { REGION_FIELDS, type useAccountForm } from '../hooks/useAccountForm';
import { useAccountSectionSave } from '../hooks/useAccountSectionSave';
import SectionSaveOutcome from './SectionSaveOutcome';
import { regionChangeText, regionCity, regionRows } from '../accountTabs';

export interface AccountRegionsTabProps {
  form: ReturnType<typeof useAccountForm>;
  /** customers:UpdateAWSAccount's level. Below it the list is read-only. */
  canEdit: boolean;
}

/**
 * The account's supported regions and its main one, the only place either is
 * edited. Saving sends these two fields and nothing else.
 */
const AccountRegionsTab: React.FC<AccountRegionsTabProps> = ({ form, canEdit }) => {
  const { isTouch } = useViewport();
  const section = useAccountSectionSave(form, REGION_FIELDS);
  const saved = form.selectedEntity;
  if (!saved) return null;

  const savedRegions = saved.supported_regions ?? [];
  const draft = form.formData.supported_regions;
  const main = form.formData.main_region;
  const rows = regionRows(savedRegions, draft, main);
  const change = regionChangeText(
    { regions: savedRegions, main: saved.main_region },
    { regions: draft, main },
  );
  const addable = AWS_REGIONS.filter((r) => !rows.some((row) => row.code === r.value));
  const buttonSx = isTouch ? TOUCH_TARGET_SX : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }} id="supported-regions-heading">
          Supported regions
        </Typography>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Box component="ul" aria-labelledby="supported-regions-heading" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {rows.map((row) => (
              <Box
                component="li"
                key={row.code}
                aria-label={`${regionCity(row.code)} ${row.code}`}
                sx={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
                  px: 1.75, py: 1, borderBottom: 1, borderColor: 'divider',
                  bgcolor: row.isMain ? 'action.hover' : undefined,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="body2"
                    color={row.removed ? 'text.secondary' : 'text.primary'}
                    sx={{ textDecoration: row.removed ? 'line-through' : 'none' }}
                  >
                    {regionCity(row.code)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{row.code}</Typography>
                </Box>
                {row.isMain && (
                  <Chip size="small" color="primary" label="Main region" />
                )}
                {!row.isMain && (row.removed || row.added) && (
                  <Typography variant="caption" sx={{ fontWeight: 500 }} color={row.removed ? 'error.main' : 'primary.main'}>
                    {row.removed ? 'Will remove' : 'Will add'}
                  </Typography>
                )}
                {canEdit && !row.isMain && !row.removed && (
                  <>
                    <Button size="small" variant="outlined" sx={buttonSx} onClick={() => form.handleChange('main_region', row.code)}>
                      Set as main
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      sx={buttonSx}
                      onClick={() => form.handleChange('supported_regions', draft.filter((c) => c !== row.code))}
                    >
                      Remove
                    </Button>
                  </>
                )}
                {canEdit && row.removed && (
                  <Button size="small" variant="outlined" sx={buttonSx} onClick={() => form.handleChange('supported_regions', [...draft, row.code])}>
                    Undo
                  </Button>
                )}
              </Box>
            ))}
          </Box>
          {canEdit && addable.length > 0 && (
            <Box sx={{ px: 1.75, py: 1.25 }}>
              <TextField
                select
                size="small"
                label="Add region"
                value=""
                sx={{ minWidth: { xs: '100%', sm: 280 } }}
                onChange={(e) => {
                  if (e.target.value) form.handleChange('supported_regions', [...draft, e.target.value]);
                }}
              >
                {addable.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          The main region holds the access roles, the global baseline and the Data baseline. Every supported
          region gets the regional baseline, and us-east-1 always gets it too, whether or not it is listed.
        </Typography>
        <SectionSaveOutcome section={section} />
      </Box>

      {canEdit && change && (
        <Box
          role="status"
          aria-label="Unsaved region changes"
          sx={{
            mt: 'auto', position: 'sticky', bottom: 0, px: { xs: 2, sm: 3 }, py: 1.5,
            borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>{change}</Typography>
          <Button variant="outlined" onClick={section.discard} disabled={section.saving}>Discard</Button>
          <Button variant="contained" onClick={section.save} disabled={section.saving}>Save</Button>
        </Box>
      )}
    </Box>
  );
};

export default AccountRegionsTab;
